/**
 * Shown to anonymous guests after their first World Cup pick. Pushes them into
 * the sign-up flow so the pick they just made survives the account conversion
 * (Supabase anonymous → real account preserves user_id and all referenced rows).
 */

import { useCallback, useMemo } from 'react';
import { Modal, Platform, Pressable, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { Trophy } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { buildPublicSignInHref } from '../../../src/signIn/helpers/postAuthRedirect.helper';
import { getGuestSignUpAfterPickPromptStyles } from './GuestSignUpAfterPickPrompt.styles';

interface GuestSignUpAfterPickPromptProps {
  visible: boolean;
  onDismiss: () => void;
  /**
   * Optional override for the "Create an account" action. When provided, it runs instead of the
   * default World-Cup sign-up navigation (used by the Monkey first-pick host to fire analytics and
   * return to the Arena). The prompt is dismissed first either way.
   */
  onSignUp?: () => void;
}

export function GuestSignUpAfterPickPrompt({ visible, onDismiss, onSignUp }: GuestSignUpAfterPickPromptProps) {
  const router = useRouter();
  const theme = useTheme();
  const styles = useMemo(() => getGuestSignUpAfterPickPromptStyles(theme), [theme]);

  const handleSignUp = useCallback(() => {
    onDismiss();
    if (onSignUp) {
      onSignUp();
      return;
    }
    const href = buildPublicSignInHref({ signup: true, returnUrl: '/app/worldcup' });
    router.push(href as `/${string}`);
  }, [onDismiss, onSignUp, router]);

  const hoverWeb = Platform.select({ web: WEB_HOVER_TRANSITION_STYLE });

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
      testID="wc-guest-signup-prompt"
    >
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <View style={styles.iconCircle}>
            <Trophy size={36} color={theme.accent} strokeWidth={2.4} />
          </View>
          <Text style={styles.title}>Don&apos;t lose your rank!</Text>
          <Text style={styles.body}>
            Create an account to save your pick, track your score, and compete on the leaderboard.
          </Text>
          <Pressable
            onPress={handleSignUp}
            style={({ pressed }) => [styles.primaryButton, hoverWeb, pressed && styles.pressed]}
            testID="wc-guest-signup-prompt-create"
          >
            <Text style={styles.primaryButtonText}>Create an account</Text>
          </Pressable>
          <Pressable
            onPress={onDismiss}
            style={({ pressed }) => [styles.secondaryButton, hoverWeb, pressed && styles.pressed]}
            testID="wc-guest-signup-prompt-dismiss"
          >
            <Text style={styles.secondaryButtonText}>Keep exploring</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}
